<script setup lang="ts">
import type { DocumentFields, Field, Form, FormSchema } from './ElectricityBills.schema'
import type { RegistrationDocument } from '~/services-v2/financial-bff/financial-bff.types'
import { cpf, formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils'
import { SolAlert, SolButton, SolInputText, SolLink, SolRadioGroup } from '@solfacil/girassol'
import { isEqual } from 'lodash'
import IconPlus from '~icons/material-symbols/add'
import IconMinus from '~icons/material-symbols/remove'
import FileStorageInput from '~/components/input/FileStorageInput.vue'
import { useFormalizationReadOnly } from '~/composables/financing/useFormalizationReadOnly'
import { RegistrationDocumentTypes } from '~/services-v2/financial-bff/__generated__/zeus'
import useRegistration from '~/store-v2/registration/useRegistration'
import { isDocFieldDisabled } from '../../Formalization.utils'
import WhatIsBondModal from '../WhatIsBondModal.vue'
import { extractErrorsByField } from './ElectricityBills.schema'

// Props
const props = defineProps<{
  form: Form
  disabled?: boolean
  hasOtherBeneficiaryUnits: boolean | undefined | null
}>()

// initial base hooks
const { t } = useI18n()
const route = useRoute<any>()

const projectId = computed(() => route.params.id as string)

const {
  data: registration,
} = useRegistration(projectId)

// initial variables and computed
const modalWhatIsBondOpen = ref(false)
const showAdditionalComments = ref<Record<number, boolean>>({})
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']

// Track documents for each electricity bill (by index)
const currentDocuments = reactive<Record<number, Record<DocumentFields, RegistrationDocument | undefined>>>({})

// Dynamic field definitions for each electricity bill index
const electricityBillFields = reactive<Record<number, any>>({})

// Function to create defineField for a specific index
function createFieldsForIndex(index: number) {
  if (!electricityBillFields[index]) {
    electricityBillFields[index] = {
      isUnderCustomerName: props.form.defineField(`electricity_bills[${index}].is_under_customer_name`),
      holderCpf: props.form.defineField(`electricity_bills[${index}].holder_cpf`),
      electricityBillDocument: props.form.defineField(`electricity_bills[${index}].electricity_bill_document`),
      employmentProofDocument: props.form.defineField(`electricity_bills[${index}].employment_proof_document`),
      additionalDocument: props.form.defineField(`electricity_bills[${index}].additional_document`),
    }
  }
  return electricityBillFields[index]
}

// Watch for changes in electricity_bills array to create fields dynamically
watch(() => props.form.values.electricity_bills?.length, (newLength) => {
  if (newLength) {
    for (let i = 0; i < newLength; i++) {
      createFieldsForIndex(i)
    }
  }
}, { immediate: true })

const isSectionDisabled = computed(() => props.disabled || false)
// Terminal read-only mode (provided by the formalization step); false by default.
const isReadOnly = useFormalizationReadOnly()
const isStatusToReviewOrInProgress = computed(() => ['to_review', 'in_progress'].includes(registration.value?.status ?? ''))
const errors = computed(() => extractErrorsByField(registration.value))

// Check for general electricity bills errors (dynamic ID errors)
const generalElectricityBillsError = computed(() => {
  return errors.value.electricity_bills || null
})

function getErrorsForUnit(index: number) {
  const unitErrors: any[] = []
  Object.entries(errors.value).forEach(([key, error]) => {
    const indexMatch = key.match(/electricity_bills\[(\d+)\]\./)
    if (indexMatch && Number(indexMatch[1]) === index) {
      unitErrors.push(error)
    }
  })
  return unitErrors
}

function getFirstErrorForUnit(index: number) {
  const unitErrors = getErrorsForUnit(index)
  return unitErrors.length > 0 ? unitErrors[0] : null
}

function getRadiosIsUnderCustomerName(index: number) {
  return [
    {
      name: `electricityBillsUnit${index}IsUnderCustomerName`,
      value: true,
      label: `${t('formAddress.yesCountryside')}`,
    },
    {
      name: `electricityBillsUnit${index}IsUnderCustomerName`,
      value: false,
      label: `${t('formAddress.noCountryside')}`,
    },
  ]
}

function handleOpenModalWhatIsBond() {
  modalWhatIsBondOpen.value = true
}

function handleShowAdditionalComments(index: number) {
  // Read-only mode: do not open the field when it has no saved document (hiding stays allowed).
  // Reopening is allowed when a document exists, so it can still be viewed/downloaded.
  const hasAdditionalDocument = !!currentDocuments[index]?.additional_document
  if (isReadOnly.value && !showAdditionalComments.value[index] && !hasAdditionalDocument) {
    return
  }
  showAdditionalComments.value[index] = !showAdditionalComments.value[index]
}

function initializeCurrentDocuments(index: number) {
  if (!currentDocuments[index]) {
    currentDocuments[index] = {
      electricity_bill_document: undefined,
      additional_document: undefined,
      employment_proof_document: undefined,
    }
  }
}

function setDocumentFieldValue(index: number, field: DocumentFields, value: string) {
  (props.form.setFieldValue as any)(`electricity_bills[${index}].${field}`, value)
}

function addElectricityBill() {
  if (isReadOnly.value) {
    return
  }
  const currentBills = props.form.values.electricity_bills || []
  if (currentBills.length < 3) {
    const newIndex = currentBills.length
    const newBills = [
      ...currentBills,
      {
        is_under_customer_name: true,
        electricity_bill_document: '',
        additional_document: '',
        holder_cpf: '',
        employment_proof_document: '',
      },
    ]
    props.form.setFieldValue('electricity_bills', newBills, false)

    // Create fields for the new index
    nextTick(() => {
      createFieldsForIndex(newIndex)
    })
  }
}

function removeElectricityBill(index: number) {
  if (isReadOnly.value) {
    return
  }
  const currentBills = props.form.values.electricity_bills || []
  if (currentBills.length > 1) {
    const updatedBills = currentBills.filter((_, i) => i !== index)
    props.form.setFieldValue('electricity_bills', updatedBills)

    // Clean up fields for removed index and reindex
    delete electricityBillFields[index]

    // Recreate fields for remaining items
    nextTick(() => {
      Object.keys(electricityBillFields).forEach((key) => {
        const keyIndex = Number(key)
        if (keyIndex > index) {
          delete electricityBillFields[keyIndex]
        }
      })

      // Recreate fields for the updated array
      for (let i = 0; i < updatedBills.length; i++) {
        createFieldsForIndex(i)
      }
    })
  }
}

watch(registration, (newValue, oldValue) => {
  if (isEqual(newValue, oldValue)) {
    return
  }

  if (newValue) {
    const data = newValue.electricity_bills ?? []

    data?.forEach((bill, index) => {
      initializeCurrentDocuments(index)

      const electricityBillDoc = bill.documents?.find(doc => doc.type === RegistrationDocumentTypes.electricity_bill)
      const employmentProofDoc = bill.documents?.find(doc => doc.type === RegistrationDocumentTypes.employment_proof)
      const additionalDoc = bill.documents?.find(doc => doc.type === RegistrationDocumentTypes.additional)

      if (electricityBillDoc) {
        currentDocuments[index].electricity_bill_document = electricityBillDoc
        setDocumentFieldValue(index, 'electricity_bill_document', electricityBillDoc.docstorage_id)
      }
      if (employmentProofDoc) {
        currentDocuments[index].employment_proof_document = employmentProofDoc
        setDocumentFieldValue(index, 'employment_proof_document', employmentProofDoc.docstorage_id)
      }
      if (additionalDoc) {
        currentDocuments[index].additional_document = additionalDoc
        setDocumentFieldValue(index, 'additional_document', additionalDoc.docstorage_id)
        showAdditionalComments.value[index] = true
      }

      const updatedBills = (props.form.values.electricity_bills || []).map((formBill, i) => {
        if (i === index) {
          return {
            ...formBill,
            is_under_customer_name: bill.is_under_customer_name,
            holder_cpf: formatCpfMasked(normalizeCpfDigits(bill.holder_cpf || '')) || '',
          }
        }
        return formBill
      })

      if (updatedBills.length > 0) {
        props.form.setValues({ electricity_bills: updatedBills } as FormSchema, false)
      }
    })

    setTimeout(() => {
      Object.entries(errors.value).forEach(([key, value]) => {
        props.form.setFieldError(key as Field, value.title || '')
      })
    }, 500)
  }
}, { immediate: true, deep: true })

watch(() => props.hasOtherBeneficiaryUnits, (hasOther) => {
  if (hasOther === false) {
    props.form.setFieldValue('electricity_bills', [])
    Object.keys(currentDocuments).forEach((key) => {
      delete currentDocuments[key]
    })
    return
  }

  if (!props.form.values.electricity_bills || props.form.values.electricity_bills.length === 0) {
    const initialBills: any = [{
      is_under_customer_name: true,
      electricity_bill_document: '',
      additional_document: '',
    }]
    props.form.setFieldValue('electricity_bills', initialBills)
    initializeCurrentDocuments(0)
  }
}, { immediate: true })
</script>

<template>
  <div v-if="hasOtherBeneficiaryUnits">
    <div class="flex flex-col items-start gap-6">
      <!-- General electricity bills error -->
      <SolAlert
        v-if="generalElectricityBillsError && isStatusToReviewOrInProgress"
        id="electricity-bills-general-error"
        class="mb-4"
        :feedback="(generalElectricityBillsError?.type as any) ?? 'warning'"
        :title="generalElectricityBillsError?.title"
      >
        {{ generalElectricityBillsError?.description }}
      </SolAlert>

      <div class="flex flex-col gap-8 w-full">
        <div
          v-for="(bill, index) in form.values.electricity_bills"
          :key="`electricity-bill-${index}`"
          class="relative"
        >
          <div class="flex justify-between items-start mb-1">
            <h4 class="font-highlight text-neutral-low-dark text-neutral-low-dark">
              {{ t('formalization.electricity_bills.bill_number', { number: index + 1 }) }}
            </h4>
            <SolButton
              v-if="(form.values.electricity_bills?.length || 0) > 1"
              id="remove-bill-button"
              size="small"
              variant="tertiary"
              :disabled="isSectionDisabled"
              @click="removeElectricityBill(index)"
            >
              <template #icon>
                <IconMinus />
              </template>
              {{ t('formalization.electricity_bills.remove_bill') }}
            </SolButton>
          </div>

          <SolAlert
            v-if="getFirstErrorForUnit(index) && isStatusToReviewOrInProgress"
            :id="`feedback-unit-${index}`"
            class="mb-4"
            :feedback="(getFirstErrorForUnit(index) as any)?.type ?? 'warning'"
            :title="getFirstErrorForUnit(index)?.title"
          >
            {{ getFirstErrorForUnit(index)?.description }}
          </SolAlert>

          <div class="flex flex-col items-start gap-4 w-full">
            <div class="flex flex-col item-start gap-1">
              <SolRadioGroup
                :id="`is_under_customer_name_${index}`"
                v-model="createFieldsForIndex(index).isUnderCustomerName[0].value"
                :title="t('formalization.electricity_bills.form.electricity_bill_customer_name')"
                :name="`electricity_bills[${index}].is_under_customer_name`"
                direction="row"
                :disabled="isSectionDisabled"
                :radios="getRadiosIsUnderCustomerName(index)"
                v-bind="createFieldsForIndex(index).isUnderCustomerName[1]"
                :error="form.errors.value[`electricity_bills[${index}].is_under_customer_name`]"
              />
            </div>

            <div class="flex items-start gap-6 w-full">
              <FileStorageInput
                :id="`electricity_bill_${index}`"
                v-model="createFieldsForIndex(index).electricityBillDocument[0].value"
                :project-id="registration?.project_id || ''"
                :name="`electricity_bills[${index}].electricity_bill_document`"
                :accept="ACCEPTED_FILE_TYPES.join(',')"
                :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments[index] || {}, 'electricity_bill_document')"
                :placeholder="t('formDocument.placeholder')"
                :label="`${t('formalization.electricity_bills.additional_unit_bill')}*`"
                v-bind="createFieldsForIndex(index).electricityBillDocument[1]"
                :error="form.errors.value[`electricity_bills[${index}].electricity_bill_document`]"
                :uploaded-file="currentDocuments[index]?.electricity_bill_document"
                :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments[index] || {}, 'electricity_bill_document')"
              />
            </div>

            <div
              v-if="!createFieldsForIndex(index).isUnderCustomerName[0].value"
              class="flex flex-col md:system:flex-row items-start gap-6 w-full"
            >
              <div class="w-full">
                <FileStorageInput
                  :id="`holder_document_${index}`"
                  v-model="createFieldsForIndex(index).employmentProofDocument[0].value"
                  :name="`electricity_bills[${index}].employment_proof_document`"
                  :project-id="registration?.project_id || ''"
                  :label="`${t('formalization.electricity_bills.form.proof_link_document')}*`"
                  :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments[index] || {}, 'employment_proof_document')"
                  :placeholder="t('formDocument.placeholder')"
                  :accept="ACCEPTED_FILE_TYPES.join(',')"
                  v-bind="createFieldsForIndex(index).employmentProofDocument[1]"
                  :error="form.errors.value[`electricity_bills[${index}].employment_proof_document`]"
                  :uploaded-file="currentDocuments[index]?.employment_proof_document"
                  :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments[index] || {}, 'employment_proof_document')"
                />

                <SolLink
                  id="what-is-bond-link"
                  size="medium"
                  @click="handleOpenModalWhatIsBond"
                >
                  {{ t('formalization.electricity_bills.what_is_proof_of_link') }}
                </SolLink>
              </div>

              <SolInputText
                :id="`holder_cpf_${index}`"
                v-model="createFieldsForIndex(index).holderCpf[0].value"
                :name="`electricity_bills[${index}].holder_cpf`"
                class="w-full md:system:w-1/2"
                :label="`${t('formalization.electricity_bills.form.document_account_holder.label')}*`"
                v-bind="createFieldsForIndex(index).holderCpf[1]"
                :mask="cpf.mask"
                :disabled="isSectionDisabled"
                :placeholder="t('formalization.electricity_bills.form.document_account_holder.placeholder')"
                :error="form.errors.value[`electricity_bills[${index}].holder_cpf`]"
              />
            </div>

            <SolLink
              :id="`add-document-link-${index}`"
              size="medium"
              @click="handleShowAdditionalComments(index)"
            >
              <template v-if="!showAdditionalComments[index]" #icon>
                <IconPlus />
              </template>
              <template v-else #icon>
                <IconPlus class="transform rotate-45" />
              </template>
              {{ showAdditionalComments[index] ? 'Esconder documento adicional' : 'Adicionar documento adicional' }}
            </SolLink>

            <div v-if="showAdditionalComments[index]" class="w-full">
              <FileStorageInput
                :id="`additional_document_${index}`"
                v-model="createFieldsForIndex(index).additionalDocument[0].value"
                :name="`electricity_bills[${index}].additional_document`"
                :project-id="registration?.project_id || ''"
                label="Documento adicional"
                :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments[index] || {}, 'additional_document')"
                :placeholder="t('formDocument.placeholder')"
                :accept="ACCEPTED_FILE_TYPES.join(',')"
                v-bind="createFieldsForIndex(index).additionalDocument[1]"
                :error="form.errors.value[`electricity_bills[${index}].additional_document`]"
                :uploaded-file="currentDocuments[index]?.additional_document"
                :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments[index] || {}, 'additional_document')"
              />
            </div>
          </div>
        </div>
      </div>

      <SolButton
        v-if="(form.values.electricity_bills?.length || 0) < 3"
        id="add-electricity-bill"
        size="medium"
        variant="tertiary"
        :disabled="isSectionDisabled"
        @click="addElectricityBill"
      >
        <template #icon>
          <IconPlus />
        </template>
        {{ t('formalization.electricity_bills.add_beneficiary_unit') }}
      </SolButton>
    </div>
  </div>

  <WhatIsBondModal
    :show="modalWhatIsBondOpen"
    @close="modalWhatIsBondOpen = false"
  />
</template>
